import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["form", "fileInput", "report", "submitBtn", "validateBtn", "modal"]

  connect() {
    this.lastImportData = null
    this.validationPassed = false
    this.resetListener = () => this.resetModal()
    this.modalTarget?.addEventListener('hidden.bs.modal', this.resetListener)
  }

  disconnect() {
    this.modalTarget?.removeEventListener('hidden.bs.modal', this.resetListener)
  }

  resetModal() {
    this.lastImportData = null
    this.validationPassed = false
    this.formTarget?.reset()
    if (this.reportTarget) {
      this.reportTarget.innerHTML = ''
    }
    this.setButtonIdle('submit')
    this.setButtonIdle('validate')
    this.updateSubmitButtonState()
  }

  validate(event) {
    event.preventDefault()

    if (!this.fileInputTarget?.files?.length) {
      alert('Please select an XLSX file')
      return
    }

    const file = this.fileInputTarget.files[0]
    if (!file.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
      alert('Please select a valid XLSX or XLS file')
      return
    }

    this.setButtonLoading('validate', 'Validating...')

    const formData = new FormData()
    formData.append('xlsx_file', file)

    fetch('/admin/credentials_bulk/validate_import.json', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(response => this.parseJson(response))
      .then(({ response, data }) => {
        // Check if validation passed (has at least one valid row to import)
        const summary = data.summary || {}
        const hasValidRows = (summary.to_create || 0) + (summary.to_update || 0) > 0

        if (response.ok) {
          // Allow import if there are valid rows (even if there are some errors)
          this.validationPassed = hasValidRows
          this.displayValidationSuccess(data)
        } else {
          this.validationPassed = false
          this.displayValidationErrors(data)
        }
        this.updateSubmitButtonState()
      })
      .catch(error => {
        console.error('Validation error:', error)
        this.displayValidationErrors({
          summary: { errors_count: 1 },
          errors_by_row: { 'General': 'Validation failed due to an unexpected error' }
        })
        this.validationPassed = false
        this.updateSubmitButtonState()
      })
      .finally(() => this.setButtonIdle('validate'))
  }

  updateSubmitButtonState() {
    if (!this.submitBtnTarget) return

    if (this.validationPassed) {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.classList.remove('btn-secondary')
      this.submitBtnTarget.classList.add('btn-success')
    } else {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.remove('btn-success')
      this.submitBtnTarget.classList.add('btn-secondary')
    }
  }

  submit(event) {
    event.preventDefault()

    if (!this.fileInputTarget?.files?.length) {
      alert('Please select an XLSX file')
      return
    }

    const file = this.fileInputTarget.files[0]
    if (!file.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
      alert('Please select a valid XLSX or XLS file')
      return
    }

    if (!this.validationPassed) {
      alert('Please validate the file first')
      return
    }

    if (!confirm('Are you sure you want to apply this import?')) return

    this.setButtonLoading('submit', 'Importing...')

    fetch(this.formTarget.action, {
      method: 'POST',
      body: new FormData(this.formTarget),
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(response => this.parseJson(response))
      .then(({ response, data }) => {
        if (response.ok) {
          this.lastImportData = data
          this.displayResults(data)
        } else {
          this.displayError(data.message || 'Import failed. Please review the spreadsheet and try again.', data.failures || data.errors)
        }
      })
      .catch(error => {
        console.error('Credential import error:', error)
        this.displayError('Import failed due to an unexpected error. Please try again.')
      })
      .finally(() => this.setButtonIdle('submit'))
  }

  displayValidationSuccess(data) {
    if (!this.reportTarget) return

    console.log('displayValidationSuccess received data:', data)

    const summary = data.summary || {}
    const toCreate = summary.to_create || 0
    const toUpdate = summary.to_update || 0
    const errorsCount = summary.errors_count || 0

    const successes = data.successes || []
    const failures = data.failures || []

    console.log('Data counts:', {
      successes: successes.length,
      failures: failures.length,
      toCreate,
      toUpdate
    })

    const toCreateList = successes.filter(s => s.action === 'created')
    const toUpdateList = successes.filter(s => s.action === 'updated')

    const createSection = toCreate > 0
      ? `<div class="alert alert-success">
          <strong><i class="bi bi-plus-circle me-2"></i>To Create (${toCreate}):</strong>
          ${this.scrollableDiv(this.renderSuccessEntries(toCreateList))}
        </div>`
      : ''

    const updateSection = toUpdate > 0
      ? `<div class="alert alert-info">
          <strong><i class="bi bi-pencil me-2"></i>To Update (${toUpdate}):</strong>
          ${this.scrollableDiv(this.renderSuccessEntries(toUpdateList))}
        </div>`
      : ''

    const errorSection = errorsCount > 0
      ? `<div class="alert alert-danger">
          <strong><i class="bi bi-exclamation-triangle me-2"></i>Validation Errors (${errorsCount}):</strong>
          ${this.scrollableDiv(this.renderFailureEntries(failures))}
        </div>`
      : ''

    const statusClass = errorsCount > 0 ? 'alert alert-warning' : 'alert alert-success'

    this.reportTarget.innerHTML = `
      <div class="${statusClass} mb-3">
        <strong>Summary:</strong><br>
        To Create: ${toCreate}; To Update: ${toUpdate}; Errors: ${errorsCount}
      </div>
      ${createSection}
      ${updateSection}
      ${errorSection}
    `
  }

  renderSuccessEntries(successList) {
    if (!Array.isArray(successList) || successList.length === 0) {
      return '<div class="text-muted">No entries</div>'
    }
    return successList.map(success => {
      const changesText = success.changes && Object.keys(success.changes).length > 0
        ? JSON.stringify(success.changes)
        : 'No changes detected'
      return `<div class="mb-1">Row ${success.row}: ${this.escape(success.email)} (${this.escape(success.publisher)}) - ${this.escape(changesText)}</div>`
    }).join('')
  }

  renderFailureEntries(failures) {
    if (!Array.isArray(failures) || failures.length === 0) {
      return '<div class="text-muted">No errors</div>'
    }
    return failures.map(failure => {
      const errorsList = Array.isArray(failure.errors) ? failure.errors : [failure.errors]
      const errorsText = errorsList.join(', ')
      return `<div class="mb-1">Row ${failure.row}: ${this.escape(failure.email)} (${this.escape(failure.publisher)}) - ${this.escape(errorsText)}</div>`
    }).join('')
  }

  renderArrayEntries(arrayData) {
    if (!Array.isArray(arrayData)) {
      console.log('renderArrayEntries: not an array', arrayData)
      return ''
    }
    if (arrayData.length === 0) {
      console.log('renderArrayEntries: empty array')
      return ''
    }
    console.log('renderArrayEntries: rendering', arrayData.length, 'entries')
    return arrayData.map(entry => {
      return `<div class="mb-2">${this.escape(entry)}</div>`
    }).join('')
  }

  displayValidationErrors(data) {
    if (!this.reportTarget) return

    const summary = data.summary || {}
    const toCreate = summary.to_create || 0
    const toUpdate = summary.to_update || 0
    const errorsCount = summary.errors_count || 0
    const failures = data.failures || []

    const errorSection = failures.length > 0
      ? `<div class="alert alert-danger">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.renderFailureEntries(failures))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger mb-3">
        <strong><i class="bi bi-x-circle me-2"></i>Validation Failed!</strong><br>
        To Create: ${toCreate}; To Update: ${toUpdate}; Errors: ${errorsCount}
      </div>
      ${errorSection}
      <div class="alert alert-warning">
        <i class="bi bi-info-circle me-2"></i>
        Please fix the errors in your spreadsheet and try again.
      </div>
    `
  }

  displayResults(data) {
    if (!this.reportTarget) return

    console.log('displayResults received data:', data)

    const summary = data.summary || {}
    const successes = data.successes || []
    const failures = data.failures || []

    const createdCount = summary.created || 0
    const updatedCount = summary.updated || 0
    const errorsCount = summary.errors_count || 0

    console.log('Data counts:', {
      successes: successes.length,
      failures: failures.length,
      createdCount,
      updatedCount,
      errorsCount
    })

    const createdList = successes.filter(s => s.action === 'created')
    const updatedList = successes.filter(s => s.action === 'updated')

    console.log('Filtered lists:', {
      createdList: createdList.length,
      updatedList: updatedList.length
    })

    const createdSection = createdCount > 0
      ? `<div class="alert alert-success">
          <strong>Created:</strong>
          ${this.scrollableDiv(this.renderSuccessEntries(createdList))}
        </div>`
      : ''

    const updatedSection = updatedCount > 0
      ? `<div class="alert alert-info">
          <strong>Updated:</strong>
          ${this.scrollableDiv(this.renderSuccessEntries(updatedList))}
        </div>`
      : ''

    const failureSection = errorsCount > 0
      ? `<div class="alert alert-danger">
          <strong>Failed:</strong>
          ${this.scrollableDiv(this.renderFailureEntries(failures))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-success mb-3">
        <strong>Import Complete!</strong><br>
        Created: ${createdCount}; Updated: ${updatedCount}; Failed: ${errorsCount}
      </div>
      ${createdSection}
      ${updatedSection}
      ${failureSection}
      <div class="text-center mt-3">
        <button type="button" class="btn btn-success" data-action="click->credential-import#downloadReport">
          <i class="bi bi-download me-2"></i>
          Download Report
        </button>
      </div>
    `
  }

  displayError(message, details = []) {
    if (!this.reportTarget) return

    const detailsHtml = Array.isArray(details) && details.length > 0
      ? this.scrollableDiv(details.map(detail => {
          if (typeof detail === 'string') return detail
          if (detail?.errors) {
            return `Row ${detail.row || '-'}: ${detail.errors.join(', ')}`
          }
          return JSON.stringify(detail)
        }).join('<br>'))
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger">
        <strong>${message}</strong>
        ${detailsHtml}
      </div>
    `
  }

  scrollableDiv(content) {
    return `<div style="max-height: 250px; overflow-y: auto;">${content}</div>`
  }

  escape(value) {
    if (value === null || value === undefined) return ''
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  setButtonLoading(buttonType, text) {
    const button = buttonType === 'validate' ? this.validateBtnTarget : this.submitBtnTarget
    if (!button) return
    button.disabled = true
    button.dataset.originalText = button.innerHTML
    button.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>${text}`
  }

  setButtonIdle(buttonType) {
    const button = buttonType === 'validate' ? this.validateBtnTarget : this.submitBtnTarget
    if (!button) return
    button.disabled = false
    if (button.dataset.originalText) {
      button.innerHTML = button.dataset.originalText
      delete button.dataset.originalText
    }
  }

  parseJson(response) {
    return response.json()
      .then(data => ({ response, data }))
      .catch(() => ({ response, data: {} }))
  }

  downloadReport(e) {
    e.preventDefault()

    if (!this.lastImportData) {
      alert('No import data available')
      return
    }

    const reportText = this.generateTextReport(this.lastImportData)
    const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `credentials_import_report_${new Date().toISOString().slice(0, 10)}.txt`
    link.click()
    URL.revokeObjectURL(url)
  }

  generateTextReport(data) {
    const summary = data.summary || {}
    const successes = data.successes || []
    const failures = data.failures || []

    const createdList = successes.filter(s => s.action === 'created')
    const updatedList = successes.filter(s => s.action === 'updated')

    const lines = [
      this.separator(),
      'CREDENTIALS IMPORT REPORT',
      this.separator(),
      `Date: ${new Date().toLocaleString()}`,
      'Status: Success',
      '',
      'SUMMARY',
      '-'.repeat(70),
      `Total Created: ${summary.created || 0}`,
      `Total Updated: ${summary.updated || 0}`,
      `Total Failed: ${summary.errors_count || 0}`,
      ''
    ]

    if (createdList.length > 0) {
      const createdEntries = createdList.map(s =>
        `Row ${s.row}: ${s.email} (${s.publisher}) - ${JSON.stringify(s.changes)}`
      )
      lines.push(
        ...this.section('CREATED CREDENTIALS', createdList.length,
          createdEntries.join('\n')
        ).split('\n')
      )
    }

    if (updatedList.length > 0) {
      const updatedEntries = updatedList.map(s =>
        `Row ${s.row}: ${s.email} (${s.publisher}) - ${JSON.stringify(s.changes)}`
      )
      lines.push(
        ...this.section('UPDATED CREDENTIALS', updatedList.length,
          updatedEntries.join('\n')
        ).split('\n')
      )
    }

    if (failures.length > 0) {
      const failureEntries = failures.map(f =>
        `Row ${f.row}: ${f.email} (${f.publisher}) - ${Array.isArray(f.errors) ? f.errors.join(', ') : f.errors}`
      )
      lines.push(
        ...this.section('FAILED CREDENTIALS', failures.length,
          failureEntries.join('\n')
        ).split('\n')
      )
    }

    lines.push(this.separator(), 'END OF REPORT', this.separator())

    return lines.join('\n')
  }

  separator() {
    return '='.repeat(70)
  }

  section(title, count, content) {
    return `\n${title} (${count})\n${'-'.repeat(70)}\n${content}\n`
  }
}
